import { type UiElementsConfigPayload } from '#/types/messages';
import { type MarkerType, type PositionHistoryClickItem } from '#/types/messages/positions-history-payload.types';

export enum DeviceType {
  DesktopBrowser = 'desktop-browser',
  MobileBrowser = 'mobile-browser',
  AndroidNative = 'android-native',
  IosNative = 'ios-native',
  Iframe = 'iframe',
}

export interface ModeConfig {
  mode: string;
  device: DeviceType;
  theme: string;
}

export enum ChartSize {
  Medium = 'medium',
  Large = 'large',
}

export interface AndroidGlobalObject {
  onTradingViewChartLoaded?: () => void;
  setChartSize?: (size: ChartSize) => ChartSize;
  openTradeConfiguration?: () => void;
  hmrIconPressed?: () => void;
  positionsHistoryMarkerPressed?: (positions: PositionHistoryClickItem[], markerType: MarkerType) => void;
  requestPositionsHistory?: (range: { from: number; to: number }) => void;
  onLineDragEnd?: (payload: LineDragEndPayload) => void;
  symbolChanged?: (symbol: string) => void;
}

type IosContextKey = 'iOSNative';

export type IosPayload =
  | {
      command: 'onTradingViewChartLoaded';
    }
  | {
      command: 'setChartSize';
      value: ChartSize;
    }
  | {
      command: 'openTradeConfiguration';
    }
  | {
      command: 'hmrIconPressed';
    }
  | {
      command: 'positionsHistoryMarkerPressed';
      value: PositionHistoryClickItem[];
      markerType: MarkerType;
    }
  | {
      command: 'requestPositionsHistory';
      value: { from: number; to: number };
    }
  | {
      command: 'onLineDragEnd';
      value: LineDragEndPayload;
    }
  | {
      command: 'symbolChanged';
      value: string;
    };

export interface IosGlobalObject {
  messageHandlers?: {
    [key in IosContextKey]?: {
      postMessage?: <R = void>(payload: IosPayload) => R;
    };
  };
}

export type LineDragEndPayload = {
  orderId: number;
  mode: 'market' | 'pending';
  field: 'stopLoss' | 'takeProfit' | 'openPrice';
  price: number;
};

export type IframePayload =
  | {
      event: 'onTradingViewChartLoaded';
    }
  | {
      event: 'setChartSize';
      payload: {
        size: ChartSize;
      };
    }
  | {
      event: 'openTradeConfiguration';
    }
  | {
      event: 'hmrIconPressed';
    }
  | {
      event: 'positionsHistoryMarkerPressed';
      payload: PositionHistoryClickItem[];
      markerType: MarkerType;
    }
  | {
      event: 'requestPositionsHistory';
      payload: { from: number; to: number };
    }
  | {
      event: 'onLineDragEnd';
      payload: LineDragEndPayload;
    }
  | {
      event: 'symbolChanged';
      payload: { symbol: string };
    };

export abstract class ParentApp {
  protected abstract get control(): unknown;

  abstract readonly serviceName: string;

  abstract isDevice(device: DeviceType): boolean;

  abstract onAppReadyListenMessages(): void;

  abstract setChartSize(size: ChartSize): ChartSize | undefined;

  abstract openUiElementsDisplayConfiguration(): void;

  abstract onHmrInfoButtonPressed(): void;

  abstract onPositionsHistoryMarkerPressed(positions: PositionHistoryClickItem[], markerType: MarkerType): void;

  abstract requestPositionsHistory(range: { from: number; to: number }): void;

  abstract onLineDragEnd(payload: LineDragEndPayload): void;

  abstract onSymbolChanged(symbol: string): void;
}

export type FeaturesOptions = {
  'chart-size'?: boolean;
  'chart-state-storage'?: boolean;
  'default-moving-averages'?: boolean;
  'trade-configuration'?: UiElementsConfigPayload['data'];
  'hmr-icon'?: boolean;
  'chart-templates'?: boolean;
};

export interface IDeviceService {
  deviceType: DeviceType;

  parentApp: ParentApp | undefined;

  featureOptions: FeaturesOptions | undefined;
}
